import { Box, ButtonBase, PersistentDrawer, Typography } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import type { CatalogItem } from '@/entities/catalog-item';
import { NEW_DATA_URL_PARAM } from '@/features/catalog/constants';
import { useFilter } from '../../../state/useFilter';
import { FILTER_DRAWER_TRANSITION_DURATION, FILTER_DRAWER_WIDTH } from '../constants';
import FilterGroup from './FilterGroup';
import FilterOptionRow from './FilterOptionRow';
import useTableFilters from './useTableFilters';

import type { Table } from '@tanstack/react-table';

const listSx = {
  height: '100%',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column'
};

const headerSx = {
  px: 1.5,
  py: 1,
  backgroundColor: 'gx.tileDark',
  borderBottom: '0.5px solid',
  borderBottomColor: 'gx.border',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const clearAllButtonSx = { color: 'gx.highlight', px: 0.2, fontSize: 12 };
const hiddenClearAllButtonSx = { ...clearAllButtonSx, visibility: 'hidden' } as const;

const TableFilters = ({
  filterOpen,
  setFilterOpen,
  table,
  height
}: {
  filterOpen: boolean;
  setFilterOpen: (newFilterOpen: boolean) => void;
  table: Table<CatalogItem>;
  height: string | number;
}) => {
  const { columnFiltersWithCounts, newDataCount } = useTableFilters(table);
  const { filters, setFilter, clearAllFilters, isAnyFilterApplied } = useFilter();

  return (
    <PersistentDrawer
      anchor='left'
      open={filterOpen}
      sx={{
        zIndex: Z_INDEX.overlay,
        height,
        flexShrink: 0,
        fontSize: 14,
        '& .MuiDrawer-paper': {
          width: FILTER_DRAWER_WIDTH,
          borderRight: '0.5px solid',
          borderRightColor: 'gx.border'
        }
      }}
      transitionDuration={FILTER_DRAWER_TRANSITION_DURATION}
      variant='persistent'
      onClose={() => {
        setFilterOpen(false);
      }}
    >
      <Box
        sx={{
          ...listSx,
          opacity: filterOpen ? 1 : 0,
          transition: `opacity ${FILTER_DRAWER_TRANSITION_DURATION}ms ease-in-out`
        }}
      >
        <Box sx={headerSx}>
          <Typography
            size={14}
            variant='h3'
          >
            Filters
          </Typography>
          <ButtonBase
            sx={isAnyFilterApplied ? clearAllButtonSx : hiddenClearAllButtonSx}
            tabIndex={isAnyFilterApplied ? 0 : -1}
            onClick={clearAllFilters}
          >
            Clear all
          </ButtonBase>
        </Box>
        <Box
          sx={{
            px: 1.5,
            borderBottom: '0.5px solid',
            borderBottomColor: 'gx.response'
          }}
        >
          <FilterOptionRow
            checked={filters[NEW_DATA_URL_PARAM]}
            count={newDataCount}
            label='New Data'
            showCount={false}
            onChange={() => {
              setFilter(NEW_DATA_URL_PARAM, !filters[NEW_DATA_URL_PARAM]);
            }}
          />
        </Box>
        {Object.entries(columnFiltersWithCounts).map(([column, options]) => (
          <FilterGroup
            key={column}
            column={column}
            options={options}
            // @ts-expect-error filter exist
            selected={filters[column]}
            onSelect={label => {
              setFilter(column, label);
            }}
          />
        ))}
      </Box>
    </PersistentDrawer>
  );
};

export default TableFilters;
